import axios, { AxiosError, AxiosHeaders } from 'axios'
import { snakeize } from 'casing'
import { createFlagsClient } from '@labrav/flags'
import devFlags from '../flags/dev/flags.json'
import { isTestEnvironment } from '../utils/testing'
import {
  engineServiceUrl,
  flagsUrl,
  flagsCacheTTLSeconds,
  identityServiceUrl,
  serviceId,
  serviceEnvironment,
} from '../utils/constants'
import { fetchCountriesListS3Url } from '../utils/flyoutConstants'
import {
  canvasAuthService,
  getCanvasAccessTokenFromStorage,
} from '../services/CanvasAuthService'
import {
  getSecureKey,
  getServiceNameFromUrl,
  classifySecureKey,
} from '../utils/helperFunctions'
import { FeatureFlags } from '../utils/featureFlags'
import { reportFrontendError, reportFrontendWarning } from '../telemetry/rum'
import { errorLogger as newrelicErrLogger } from '../utils/ErrorHandler'
import type { G2ReviewResponseType } from '../types'
import type {
  IdentityServiceUser,
  UserProfile,
} from '../modules/userProfile/userProfileTypes'
import { defaultErrorDisposition } from './errorDisposition'
import type { ClassifyApiError } from './errorDisposition'
import {
  getValidAuth0AccessToken,
  hasAuth0TokenGetter,
  isAuth0LogoutInFlight,
  isInvalidAccessTokenResponse,
  recoverFromInvalidAccessToken,
} from './auth'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Optional per-request error classifier for telemetry disposition.
     * See `ClassifyApiError` / `ApiErrorDisposition` in `./errorDisposition`:
     * `'ignore'` skips telemetry, `'warning'`/`'error'` set severity, and
     * `undefined` falls back to `defaultErrorDisposition`. Omit when the
     * default policy is enough. Rejection behavior is unchanged.
     */
    classifyError?: ClassifyApiError
    authSource?:
      | 'auth0'
      | 'secure-key'
      | 'labra-external'
      | 'legacy-internal'
      | 'canvas'
      | 'caller'
      | 'none'
    _authRetry?: boolean
  }
}

export const API = axios.create({ timeout: 30000 })

/** Presigned S3 POST/PUT must not carry Labra auth headers — S3 rejects them with 400. */
export const isS3PresignedRequestUrl = (url: string): boolean =>
  /\.s3(\.[a-z0-9-]+)?\.amazonaws\.com/i.test(url)

const flagsClient = createFlagsClient({
  service: serviceId,
  env: serviceEnvironment,
  flagsUrl,
  cacheTtlSeconds: flagsCacheTTLSeconds,
  flagsJson: import.meta.env.DEV ? devFlags : undefined,
})

const sameOriginApiEnabled: Promise<boolean> = flagsClient
  .getFlagValue(FeatureFlags.SAME_ORIGIN_API)
  .catch(() => false)

export const setOnboardingWelcome = (isWelcome: boolean) => {
  try {
    localStorage.setItem('onboardingWelcome', JSON.stringify(isWelcome))
  } catch (err) {
    console.warn('localStorage is not available.')
    newrelicErrLogger(err as Error, {
      message: err,
    })
    return undefined
  }
}
if (!isTestEnvironment()) {
  API.interceptors.request.use(async config => {
    if (config._authRetry && config.authSource === 'auth0') return config

    const originalRequestUrl = config.url || ''
    if ((await sameOriginApiEnabled) && config.url) {
      config.url = config.url.replace(
        /^https?:\/\/(qa\.|stg\.)?api\.labra\.io\//,
        `${window.location.origin}/api/`
      )
    }

    const requestUrl = config.url || originalRequestUrl
    if (isS3PresignedRequestUrl(requestUrl)) {
      const headers = AxiosHeaders.from(config.headers)
      headers.delete('Authorization')
      headers.delete('Internal-Authorization')
      config.headers = headers
      config.authSource = 'none'
      return config
    }
    const headers = AxiosHeaders.from(config.headers)

    config.authSource = 'none'
    const serviceName =
      getServiceNameFromUrl(originalRequestUrl) ||
      getServiceNameFromUrl(config.url || '')
    const secureKey = getSecureKey()
    // HubSpot / Collab Room / embed JWT in URL must win over Auth0 or Canvas tokens.
    // Otherwise aws_engine returns 403 "invalid access token" when Auth0 is in localStorage.
    const secureKeyType = secureKey ? classifySecureKey(secureKey) : 'invalid'
    if (secureKeyType !== 'invalid') {
      if (
        !headers.has('Authorization') &&
        secureKeyType === 'auth0'
      ) {
        headers.set('Authorization', `Bearer ${secureKey}`)
        config.authSource = 'secure-key'
        config.headers = headers
        return config
      }

      if (
        !headers.has('Authorization') &&
        secureKeyType === 'labra-external'
      ) {
        headers.set('Authorization', `Bearer ${secureKey}`)
        config.authSource = 'labra-external'
        config.headers = headers
        return config
      }

      if (secureKeyType === 'legacy-internal' && serviceName) {
        headers.set(
          'Internal-Authorization',
          `Bearer-${serviceName} ${secureKey}`
        )
        config.authSource = 'legacy-internal'
        config.headers = headers
        return config
      }
    }

    const hasCallerAuthorization = headers.has('Authorization')
    // Prefer localStorage: the host app may initialize a different
    // CanvasAuthService singleton and persist the token for this shared interceptor.
    const canvasJWT =
      getCanvasAccessTokenFromStorage() || canvasAuthService.getAccessToken()
    if (canvasJWT) {
      // Check environment variable to determine header type
      // VITE_SALESFORCE_USE_AUTH0=true: Use standard Authorization header (for testing with Auth0 tokens)
      // VITE_SALESFORCE_USE_AUTH0=false: Use Internal-Authorization header (for production with backend JWTs)
      const useAuth0 = import.meta.env.VITE_SALESFORCE_USE_AUTH0 === 'true'

      if (useAuth0 && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${canvasJWT}`)
      } else if (!useAuth0 && serviceName) {
        headers.set(
          'Internal-Authorization',
          `Bearer-${serviceName} ${canvasJWT}`
        )
      }

      config.authSource = hasCallerAuthorization ? 'caller' : 'canvas'
      config.headers = headers
      return config
    }
    if (headers.has('Authorization')) {
      config.authSource = 'caller'
      config.headers = headers
      return config
    }

    // Auth0 login → access token. If expired, getAccessTokenSilently uses the
    // refresh token. If refresh is impossible, logout.
    const token = await getValidAuth0AccessToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
      config.authSource = 'auth0'
    } else if (hasAuth0TokenGetter()) {
      return Promise.reject(new Error('Auth0 session expired'))
    }

    config.headers = headers
    return config
  })

  API.interceptors.response.use(
    res => {
      return res
    },
    async (error: AxiosError) => {
      const config = error.config
      // 401, or 403+"invalid access token" → force Auth0 refresh once and retry.
      // On a second Auth0 failure, only logout when the response is still an
      // invalid-token signal (not a leftover permission/RBAC 403).
      if (
        config?.authSource === 'auth0' &&
        isInvalidAccessTokenResponse(error.response)
      ) {
        if (!config._authRetry) {
          const recovered = await recoverFromInvalidAccessToken({
            alreadyRetried: false,
            response: error.response,
          })
          if (recovered) {
            config._authRetry = true
            config.authSource = 'auth0'
            config.headers = AxiosHeaders.from(config.headers)
            config.headers.set('Authorization', `Bearer ${recovered}`)
            return API(config)
          }
        } else {
          await recoverFromInvalidAccessToken({
            alreadyRetried: true,
            response: error.response,
          })
        }
      }

      // Avoid flooding Oodle while redirecting to logout after a stale session.
      if (isAuth0LogoutInFlight()) {
        return Promise.reject(error)
      }

      const disposition =
        config?.classifyError?.(error) ?? defaultErrorDisposition(error)

      if (disposition === 'ignore') {
        return Promise.reject(error)
      }

      const requestId = error.response?.headers['x-request-id']
      const { url: requestURL, method: requestMethod } =
        error.response?.config || {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // newrelicErrLogger((error.response?.data as any)?.message || error, {
      //   requestId: requestId || 'No Request Id',
      //   requestURL: requestURL || 'NO Request URL',
      //   requestMethod: requestMethod || 'NO Request Method',
      // })
      const requestData = error.response?.config?.data ?? error.config?.data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiMessage = (error.response?.data as any)?.message ?? ''
      const attributes = {
        'http.status': String(error.response?.status ?? 'network_error'),
        'http.url': requestURL ?? 'unknown',
        'http.method': requestMethod?.toUpperCase() ?? 'unknown',
        'http.request_id': requestId ?? '',
        'http.error_message': apiMessage,
        'http.request_body':
          typeof requestData === 'string'
            ? requestData
            : JSON.stringify(requestData ?? null),
        'http.response_body': JSON.stringify(error.response?.data ?? null),
      }

      if (disposition === 'warning') {
        reportFrontendWarning('api.error', error, attributes)
      } else {
        reportFrontendError('api.error', error, attributes)
      }
      return Promise.reject(error)
    }
  )
}

export const fetchAwsCountriesList = () => axios.get(fetchCountriesListS3Url)

export const patchUserProfile = (
  partnerId: string,
  oktaUserId: string,
  profile: UserProfile
) =>
  API.patch(
    `${engineServiceUrl}/partners/${partnerId}/profiles/${oktaUserId}`,
    snakeize(profile)
  )

export const patchUserProfileIdentityService = (
  partnerId: string,
  userId: string,
  profile: Partial<IdentityServiceUser> | UserProfile
) =>
  API.patch(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}`,
    profile
  )

export const fetchUserProfile = (
  partnerId: string,
  userId: string
): Promise<IdentityServiceUser> =>
  API.get(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`).then(
    ({ data }) => data as IdentityServiceUser
  )

export const getG2ReviewUrl = (partnerId: string, userId: string) =>
  API.get(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}/review_urls`
  )

export const patchUserWalkthrough = (
  partnerId: string,
  userId: string,
  data: Record<string, unknown>
) =>
  API.patch(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`, data)

export const putCustomerReviewG2 = (partnerId: string, data: object) =>
  API.put(
    `${identityServiceUrl}/partners/${partnerId}/customer-reviews/g2`,
    data
  )

export const getG2ReviewData = async (
  partnerId: string
): Promise<G2ReviewResponseType[]> => {
  const response = await API.get(
    `${identityServiceUrl}/partners/${partnerId}/customer-reviews`
  )

  return response.data.results.map(
    (item: {
      id: string
      completed: boolean
      opened_at: string
      review_type: string
      partner_id: string
    }) => ({
      id: item.id,
      completed: item.completed,
      openedAt: item.opened_at,
      reviewType: item.review_type,
      partnerId: item.partner_id,
    })
  )
}
